import type { User } from '@supabase/supabase-js';
import { getCurrentOrganisationId, isMockMode, supabase } from './supabaseClient';

const PENDING_KEY = 'pcnguard_fleet_trial_signup_metadata_v1';

export type FleetTrialSignupMetadata = {
  product?: string | null;
  trial?: string | null;
  duration?: string | null;
  source?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  company_name?: string | null;
  phone?: string | null;
  consent_to_contact?: boolean;
  consent_text?: string | null;
  consent_captured_at?: string | null;
};

export const FLEETMODE_CONSENT_TEXT =
  'I agree to be contacted by PCNGuard about my FleetMode trial, including help getting started and plan options.';

const trimOrNull = (value: string | null | undefined) => {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
};

export const getFleetTrialParams = (search: string): FleetTrialSignupMetadata => {
  const params = new URLSearchParams(search);
  return {
    product: trimOrNull(params.get('product')),
    trial: trimOrNull(params.get('trial')),
    duration: trimOrNull(params.get('duration')),
    source: trimOrNull(params.get('source')),
    utm_source: trimOrNull(params.get('utm_source')),
    utm_medium: trimOrNull(params.get('utm_medium')),
    utm_campaign: trimOrNull(params.get('utm_campaign')),
    utm_content: trimOrNull(params.get('utm_content')),
    utm_term: trimOrNull(params.get('utm_term')),
  };
};

export const isFleetTrialSignup = (metadata: FleetTrialSignupMetadata) =>
  metadata.product === 'fleetmode' && metadata.trial === '3pcn-ai-control';

export const persistPendingFleetTrialMetadata = (metadata: FleetTrialSignupMetadata) => {
  if (!isFleetTrialSignup(metadata)) return;
  window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(metadata));
};

export const readPendingFleetTrialMetadata = (): FleetTrialSignupMetadata | null => {
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as FleetTrialSignupMetadata) : null;
  } catch {
    return null;
  }
};

export const clearPendingFleetTrialMetadata = () => {
  window.sessionStorage.removeItem(PENDING_KEY);
};

export const metadataFromUser = (user: User): FleetTrialSignupMetadata => ({
  product: trimOrNull(user.user_metadata?.product),
  trial: trimOrNull(user.user_metadata?.trial),
  duration: trimOrNull(user.user_metadata?.duration),
  source: trimOrNull(user.user_metadata?.source),
  utm_source: trimOrNull(user.user_metadata?.utm_source),
  utm_medium: trimOrNull(user.user_metadata?.utm_medium),
  utm_campaign: trimOrNull(user.user_metadata?.utm_campaign),
  utm_content: trimOrNull(user.user_metadata?.utm_content),
  utm_term: trimOrNull(user.user_metadata?.utm_term),
  first_name: trimOrNull(user.user_metadata?.first_name),
  last_name: trimOrNull(user.user_metadata?.last_name),
  full_name: trimOrNull(user.user_metadata?.full_name || user.user_metadata?.name),
  company_name: trimOrNull(user.user_metadata?.company_name),
  phone: trimOrNull(user.user_metadata?.phone),
  consent_to_contact: user.user_metadata?.consent_to_contact === true,
  consent_text: trimOrNull(user.user_metadata?.consent_text),
  consent_captured_at: trimOrNull(user.user_metadata?.consent_captured_at),
});

export const initializeFleetTrial = async (metadata: FleetTrialSignupMetadata) => {
  if (isMockMode || !isFleetTrialSignup(metadata)) return;

  const organisationId = await getCurrentOrganisationId();
  const { data, error } = await supabase.functions.invoke('initialize-fleet-trial', {
    body: {
      organisation_id: organisationId,
      ...metadata,
    },
  });

  if (error) throw error;
  if (data && data.ok === false) throw new Error(data.error || 'Failed to initialize FleetMode trial');
};

export const initializeFleetTrialForUser = async (user: User) => {
  const pending = readPendingFleetTrialMetadata();
  const metadata = {
    ...metadataFromUser(user),
    ...(pending ?? {}),
  };

  if (!isFleetTrialSignup(metadata)) return;
  await initializeFleetTrial(metadata);
  clearPendingFleetTrialMetadata();
};
